import React, { useState } from "react";
import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase/config";

const NotificationTest = () => {
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(false);
  const [testResult, setTestResult] = useState("");

  const testFunctionConnection = async () => {
    setLoading(true);
    setStatus("Testing function connection...");
    
    try {
      console.log("🔍 Testing functions object:", functions);
      const testFunction = httpsCallable(functions, "sendNotificationToCustomers");
      console.log("🔍 Test function created:", testFunction);
      
      setStatus("✅ Functions object is properly initialized");
      setTestResult("Functions connection test passed");
      
    } catch (error) {
      console.error("❌ Function connection test failed:", error);
      setStatus("❌ Function connection test failed");
      setTestResult(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const testNotificationCall = async () => {
    setLoading(true);
    setStatus("Testing notification call...");
    
    try {
      const sendToCustomers = httpsCallable(functions, "sendNotificationToCustomers");
      
      console.log("🔍 Calling sendNotificationToCustomers...");
      const result = await sendToCustomers({ 
        title: "🧪 Test Notification", 
        body: "This is a test notification from the debug component",
        type: "test"
      });
      
      console.log("✅ Function call result:", result);
      setStatus("✅ Notification function call successful");
      setTestResult(`Success: ${JSON.stringify(result.data, null, 2)}`);
      
    } catch (error) {
      console.error("❌ Notification function call failed:", error);
      setStatus("❌ Notification function call failed");
      setTestResult(`Error: ${error.message}\nCode: ${error.code}\nDetails: ${JSON.stringify(error.details, null, 2)}`);
    } finally {
      setLoading(false);
    }
  };

  const checkFirebaseConfig = () => {
    setStatus("Checking Firebase configuration...");
    
    try {
      console.log("🔍 Firebase config check:");
      console.log("- Functions object:", functions);
      console.log("- Functions app:", functions.app);
      console.log("- Functions region:", functions.region);
      
      setStatus("✅ Firebase config check completed");
      setTestResult("Check browser console for detailed Firebase config information");
      
    } catch (error) {
      console.error("❌ Firebase config check failed:", error);
      setStatus("❌ Firebase config check failed");
      setTestResult(`Error: ${error.message}`);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm p-6 border border-blue-200">
      <h2 className="text-xl font-bold text-blue-700 mb-4">🧪 Notification System Test</h2>
      
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button
            onClick={checkFirebaseConfig}
            className="bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors"
          >
            🔍 Check Firebase Config
          </button>
          
          <button
            onClick={testFunctionConnection}
            disabled={loading}
            className="bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 disabled:bg-gray-400 transition-colors"
          >
            🔗 Test Function Connection
          </button>
          
          <button
            onClick={testNotificationCall}
            disabled={loading}
            className="bg-purple-600 text-white py-2 px-4 rounded-lg hover:bg-purple-700 disabled:bg-gray-400 transition-colors"
          >
            📤 Test Notification Call
          </button>
        </div>

        {status && (
          <div className={`p-3 rounded-lg text-sm ${
            status.includes("✅") 
              ? "bg-green-50 text-green-700 border border-green-200" 
              : status.includes("❌") 
                ? "bg-red-50 text-red-700 border border-red-200"
                : "bg-blue-50 text-blue-700 border border-blue-200"
          }`}>
            {status}
          </div>
        )}

        {testResult && (
          <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
            <h3 className="font-semibold text-gray-800 mb-2">Test Results:</h3>
            <pre className="text-xs text-gray-700 whitespace-pre-wrap overflow-x-auto">
              {testResult}
            </pre>
          </div>
        )}

        <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
          <h3 className="font-semibold text-yellow-800 mb-2">🔧 Troubleshooting Steps:</h3>
          <ol className="text-sm text-yellow-700 space-y-1">
            <li>1. Check browser console for detailed error messages</li>
            <li>2. Verify Firebase functions are deployed: <code>firebase deploy --only functions</code></li>
            <li>3. Ensure you're logged in as admin with proper role</li>
            <li>4. Check if functions are in the correct region (us-central1)</li>
            <li>5. Verify no CORS issues in network tab</li>
          </ol>
        </div>
      </div>
    </div>
  );
};

export default NotificationTest; 